/**
 * PRP-FEEDBACK-2 (B-7515): the imported estimate's name at the head of the estimate meta grid,
 * editable inline like the O&P pair beside it. Saving writes `metadata.estimate_name` through
 * PATCH /imports/{hash}/metadata (`name`); the next export's project name follows (sanitized).
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { updateImportName } from '../../lib/importMetadataApi';
import { Button } from './ui/button';
import { Input } from './ui/input';

const MAX = 80;

interface Props {
  parseHash: string;
  /** The display name the server gave (`name` on GET /imports/{hash}), else the stored name. */
  name: string | null | undefined;
  /** Called with the saved name so the page can show it elsewhere. */
  onRenamed?: (_name: string) => void;
}

export default function ImportNameField({ parseHash, name, onRenamed }: Props) {
  const [shown, setShown] = useState(name || 'Imported estimate');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'ok' | 'bad'; msg: string } | null>(null);

  useEffect(() => {
    setShown(name || 'Imported estimate');
    setEditing(false);
    setFlash(null);
  }, [name, parseHash]);

  function startEdit() {
    setDraft(shown);
    setFlash(null);
    setEditing(true);
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    const next = draft.trim().replace(/\s+/g, ' ');
    if (!next || next.length > MAX) {
      setFlash({ kind: 'bad', msg: `The name must be 1 to ${MAX} characters.` });
      return;
    }
    if (next === shown) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setFlash(null);
    try {
      const out = await updateImportName(parseHash, next);
      const saved = out.name || next;
      setShown(saved);
      setEditing(false);
      setFlash({ kind: 'ok', msg: 'Renamed. The next export uses this name.' });
      onRenamed?.(saved);
    } catch (err: unknown) {
      const status = (err as { status?: number } | null)?.status;
      setFlash({
        kind: 'bad',
        msg:
          status === 409
            ? 'The estimate changed while you were editing. Reload and try again.'
            : 'Could not rename. The name is unchanged.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="import-name" className="flex min-w-0 flex-wrap items-center gap-2">
      {!editing ? (
        <>
          <span className="font-[var(--font-mono)] text-[13px] font-semibold text-[var(--tx-hi)] truncate" title={shown}>
            {shown}
          </span>
          <Button size="sm" variant="ghost" type="button" onClick={startEdit} aria-label="Rename estimate">
            Rename
          </Button>
        </>
      ) : (
        <form className="flex flex-wrap items-center gap-[6px]" onSubmit={onSave} aria-label="Estimate name">
          <Input
            aria-label="Estimate name"
            className="w-[260px] h-[28px] px-[7px]"
            value={draft}
            maxLength={MAX}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
            autoFocus
          />
          <Button size="sm" variant="primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </Button>
        </form>
      )}
      {flash && (
        <p
          role="status"
          data-testid="import-name-flash"
          className={`m-0 basis-full text-[11.5px] ${flash.kind === 'ok' ? 'text-[var(--ok)]' : 'text-[var(--bad)]'}`}
        >
          {flash.msg}
        </p>
      )}
    </div>
  );
}
